import React from "react";
import { useField, useFormikContext } from "formik";
import { toast } from "react-toastify";
import ToastMessage, { toastConfig } from "../../../../helpers/ToastMessage/ToastMessage";
import {
  validateFile,
  cleanFileExtension,
  getDevTestFileUrl,
} from "./FilePreviewerFieldRenderer.utils";
import { FormField } from "@avamae/formbuilder";
import { INVOICE_ACCEPTED_FILE_TYPES } from "../../Invoice/Invoice.config";

/**
 * Hook to handle file upload functionality for FilePreviewer
 */
export const useFileUpload = (field: FormField<any>, enableUpload: boolean) => {
  const { setFieldValue } = useFormikContext<any>();

  const handleFileUpload = (file: File) => {
    const errors = validateFile(file);

    if (errors.length > 0) {
      // Show error toast
      toast(
        React.createElement(ToastMessage, {
          toastData: errors.map((error) => ({
            title: "Upload Error",
            text: error,
          })),
          className: "ToastError",
        }),
        { ...toastConfig }
      );
      return;
    }

    try {
      // Extract file information
      const fileName = file.name;

      // More robust file extension extraction
      const lastDotIndex = fileName.lastIndexOf(".");
      const fileExtension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : "";

      if (!fileExtension) {
        throw new Error("File must have a valid extension");
      }

      // Update form fields with new file information
      setFieldValue("fileName", fileName);
      setFieldValue("fileExtension", fileExtension);
      setFieldValue("filePath", fileName);

      // Set the file object for form submission and preview
      setFieldValue("file", file);

      toast(
        React.createElement(ToastMessage, {
          toastData: [
            {
              title: "Invoice file changed",
              text: `Submit form to complete upload.`,
            },
          ],
          className: "ToastSuccess",
        }),
        { ...toastConfig }
      );
    } catch (error) {
      console.error("File upload error:", error);
      toast(
        React.createElement(ToastMessage, {
          toastData: [
            {
              title: "Error",
              text: "Failed to attach the file. Please try again.",
            },
          ],
          className: "ToastError",
        }),
        { ...toastConfig }
      );
    }
  };

  return {
    handleFileUpload: enableUpload ? handleFileUpload : undefined,
  };
};

/**
 * Hook to process file URL and type for FilePreviewer
 */
export const useFilePreviewerData = (field: FormField<any>) => {
  const [{ value }] = useField(field.name as string);
  const { values } = useFormikContext<any>();

  // Extract file URL and type from the field value and form values
  let fileUrl = value || "";

  // Get the actual file object if it exists (for newly uploaded files)
  const fileObject = values.file instanceof File ? values.file : null;

  // Clean file extension by removing leading dot if present
  const rawFileType = values.fileExtension || "";
  const fileType = cleanFileExtension(rawFileType);

  // In development mode, use local test files to bypass CORS
  // const devTestFileUrl = getDevTestFileUrl(fileType);
  // if (devTestFileUrl) {
  //   fileUrl = devTestFileUrl;
  // }

  return {
    fileUrl,
    fileType,
    fileObject,
    acceptedFileTypes: INVOICE_ACCEPTED_FILE_TYPES.join(", ").replace(/image\//g, "."),
  };
};
